// frontend/src/components/formularios/campos/TablaSeguimientoMensual.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  AssignmentTurnedIn as AssignmentIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  DateRange as DateRangeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es';
import CampoModal from './CampoModal';
import formularioService from '../../../services/formularioService';
import { format, addDays } from 'date-fns';

const TablaSeguimientoMensual = ({ 
  campo, 
  value = {}, 
  onChange,
  parentRespuestaId,
  disabled = false 
}) => {
  const { configuracion } = campo;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [objetivosDisponibles, setObjetivosDisponibles] = useState([]);
  const [seguimientos, setSeguimientos] = useState([]);
  const [mesTrabajo, setMesTrabajo] = useState(value?.mes_trabajo || new Date().toISOString().split('T')[0]);
  const [dialogSeleccionOpen, setDialogSeleccionOpen] = useState(false);
  
  // NUEVO: Estados para agregar sesiones individuales
  const [objetivoSeleccionado, setObjetivoSeleccionado] = useState('');
  
  useEffect(() => {
 
    if (value && Object.keys(value).length > 0) {
      // Manejar migración de estructura anterior (objeto) a nueva (array)
      if (value.seguimientos !== undefined) {
        let seguimientosNormalizados = [];
        
        if (Array.isArray(value.seguimientos)) {
          // Nueva estructura: ya es array
          seguimientosNormalizados = value.seguimientos;
        } else if (typeof value.seguimientos === 'object' && value.seguimientos !== null) {
          // Estructura anterior: convertir objeto a array
          seguimientosNormalizados = Object.entries(value.seguimientos).flatMap(([objetivoId, sesiones]) => {
            if (Array.isArray(sesiones)) {
              return sesiones.map((sesion, index) => ({
                id: `migrado_${objetivoId}_${index}_${Date.now()}`,
                objetivo_id: objetivoId,
                fecha: sesion.fecha || new Date().toISOString().split('T')[0],
                actividades: sesion.actividades || '',
                estado: sesion.estado || '',
                observaciones: sesion.observaciones || '',
                editando: false
              }));
            }
            return [];
          });
        }
        
        setSeguimientos(seguimientosNormalizados);
       }
      
      if (value.mes_trabajo !== undefined) {
        setMesTrabajo(value.mes_trabajo);
      }
    }
  }, [value]);
  
  // Cargar objetivos disponibles cuando se tiene parentRespuestaId
  useEffect(() => {
    const cargarObjetivosDisponibles = async () => {
      if (!parentRespuestaId) {
        console.warn('No hay parentRespuestaId para cargar objetivos');
        return;
      }
      
      try {
        setLoading(true);
 
        const response = await formularioService.getObjetivosDisponibles(parentRespuestaId);
         
        if (response.success && response.data) {
          const objetivos = response.data.objetivos_disponibles || [];
          setObjetivosDisponibles(objetivos);
         } else {
          console.error('Error en respuesta de objetivos:', response);
          setObjetivosDisponibles([]);
        }
      } catch (error) {
        console.error('Error al cargar objetivos disponibles:', error);
        setObjetivosDisponibles([]);
      } finally {
        setLoading(false);
      }
    };
    
    cargarObjetivosDisponibles();
  }, [parentRespuestaId]);
  
  // Actualizar valor - COMPLETAMENTE LIBRE
  const actualizarValor = (nuevosSeguimientos = null) => {
    const nuevoValor = {
      mes_trabajo: mesTrabajo,
      seguimientos: nuevosSeguimientos !== null ? nuevosSeguimientos : seguimientos,
      // SIN estado cerrado, SIEMPRE editable
    };

    onChange(nuevoValor);
  };
  
  // NUEVO: Agregar una sesión individual para cualquier objetivo
  const agregarSesionLibre = () => {
    if (!objetivoSeleccionado) return;
    
    const nuevaSesion = {
      id: `sesion_${Date.now()}`,
      objetivo_id: objetivoSeleccionado,
      fecha: new Date().toISOString().split('T')[0],
      actividades: '',
      estado: '',
      observaciones: '',
      editando: true
    };
    
    const nuevosSeguimientos = [...seguimientos, nuevaSesion];
    setSeguimientos(nuevosSeguimientos);
    setObjetivoSeleccionado(''); // Limpiar selección
    
    // NO actualizar valor mientras se está editando
  };
  
  // NUEVO: Agregar múltiples sesiones desde el diálogo
  const agregarSesionesSeleccionadas = (objetivosSeleccionados) => {
    const nuevasSesiones = objetivosSeleccionados.map(objId => ({
      id: `sesion_${Date.now()}_${objId}`,
      objetivo_id: objId,
      fecha: new Date().toISOString().split('T')[0],
      actividades: '',
      estado: '',
      observaciones: '',
      editando: true
    }));
    
    const nuevosSeguimientos = [...seguimientos, ...nuevasSesiones];
    setSeguimientos(nuevosSeguimientos);
    setDialogSeleccionOpen(false);
  };
  
  const actualizarSeguimiento = (sesionId, campo, valor) => {
    const nuevosSeguimientos = seguimientos.map(sesion => 
      sesion.id === sesionId 
        ? { ...sesion, [campo]: valor }
        : sesion
    );
    setSeguimientos(nuevosSeguimientos);
  };
  
  const guardarSeguimiento = (sesionId) => {
    const nuevosSeguimientos = seguimientos.map(sesion => 
      sesion.id === sesionId 
        ? { ...sesion, editando: false }
        : sesion
    );
    setSeguimientos(nuevosSeguimientos);
    
    // Actualizar valor con los cambios
    actualizarValor(nuevosSeguimientos);
  };
  
  const eliminarSeguimiento = (sesionId) => {
    const nuevosSeguimientos = seguimientos.filter(sesion => sesion.id !== sesionId);
    setSeguimientos(nuevosSeguimientos);
    actualizarValor(nuevosSeguimientos);
  };
  
  const getColorEstado = (estado) => {
    switch (estado) {
      case 'logrado': return 'success';
      case 'en_proceso': return 'warning';
      case 'no_logrado': return 'error';
      default: return 'default';
    }
  };
  
  const getIconEstado = (estado) => {
    switch (estado) {
      case 'logrado': return <CheckIcon />;
      case 'en_proceso': return <WarningIcon />;
      default: return null;
    }
  };
  
  // Calcular estadísticas simples
  const calcularEstadisticas = () => {
    // Validación adicional para asegurar que seguimientos es un array
    const seguimientosArray = Array.isArray(seguimientos) ? seguimientos : [];
    
    let logrados = 0;
    let enProceso = 0;
    let noLogrados = 0;
    
    seguimientosArray.forEach(sesion => {
      if (sesion.estado === 'logrado') logrados++;
      else if (sesion.estado === 'en_proceso') enProceso++;
      else if (sesion.estado === 'no_logrado') noLogrados++;
    });
    
    return { 
      total: seguimientosArray.length, 
      logrados, 
      enProceso, 
      noLogrados,
      objetivosUnicos: [...new Set(seguimientosArray.map(s => s.objetivo_id))].length
    };
  };
  
  // Agrupar sesiones por objetivo para mejor visualización
  const agruparPorObjetivo = () => {
    // Validación adicional para asegurar que seguimientos es un array
    const seguimientosArray = Array.isArray(seguimientos) ? seguimientos : [];
    
    const grupos = {};
    seguimientosArray.forEach(sesion => {
      if (!grupos[sesion.objetivo_id]) {
        grupos[sesion.objetivo_id] = [];
      }
      grupos[sesion.objetivo_id].push(sesion);
    });
    return grupos;
  };
  
  const stats = calcularEstadisticas();
  const gruposPorObjetivo = agruparPorObjetivo();
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box>
        {/* Header con información del mes */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="h6" color="primary">
                  Seguimiento Libre - Psicoterapia
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Período: {format(new Date(mesTrabajo), 'dd MMMM yyyy', { locale: es })} | Modalidad: Sin restricciones
                </Typography>
                <Typography variant="caption" color="success.main" fontWeight="bold">
                  ✅ Puede trabajar cualquier objetivo las veces que necesite
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip 
                  icon={<AssignmentIcon />}
                  label={`${stats.total} sesiones`}
                  color="primary"
                  size="small"
                />
                <Chip 
                  icon={<CheckIcon />}
                  label={`${stats.logrados} logradas`}
                  color="success"
                  size="small"
                />
                <Chip 
                  icon={<WarningIcon />}
                  label={`${stats.enProceso} en proceso`}
                  color="warning"
                  size="small"
                />
                <Chip 
                  label={`${stats.objetivosUnicos} objetivos`}
                  color="info"
                  size="small"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        
        {/* Controles para agregar sesiones */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Agregar Nuevas Sesiones
          </Typography>
          
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center">
            {/* Selección rápida de objetivo */}
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <Select
                value={objetivoSeleccionado}
                onChange={(e) => setObjetivoSeleccionado(e.target.value)}
                displayEmpty
                placeholder="Seleccionar objetivo..."
              >
                <MenuItem value="">
                  <em>Seleccionar objetivo para nueva sesión...</em>
                </MenuItem>
                
                {Object.entries(
                  objetivosDisponibles.reduce((acc, obj) => {
                    if (!acc[obj.area_nombre]) acc[obj.area_nombre] = [];
                    acc[obj.area_nombre].push(obj);
                    return acc;
                  }, {})
                ).map(([area, objetivos]) => [
                  <MenuItem key={`area-${area}`} disabled sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    📋 {area}
                  </MenuItem>,
                  ...objetivos.map(objetivo => (
                    <MenuItem key={objetivo.id} value={objetivo.id} sx={{ pl: 4 }}>
                      Obj. {objetivo.objetivo_numero}: {
                        objetivo.objetivo_texto.length > 60 ? 
                          objetivo.objetivo_texto.substring(0, 60) + '...' :
                          objetivo.objetivo_texto
                      }
                    </MenuItem>
                  ))
                ])}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={agregarSesionLibre}
              disabled={!objetivoSeleccionado || disabled}
            >
              Agregar Sesión
            </Button>
            
            <Divider orientation="vertical" flexItem />
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogSeleccionOpen(true)}
              disabled={disabled}
            >
              Selección Múltiple
            </Button>
            
            <Tooltip title="Recargar objetivos disponibles">
              <IconButton 
                color="primary" 
                onClick={() => window.location.reload()}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
        
        {/* Tabla de seguimientos LIBRE */}
        {Array.isArray(seguimientos) && seguimientos.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width={isMobile ? "25%" : "20%"}>Objetivo</TableCell>
                  <TableCell width={isMobile ? "15%" : "12%"}>Fecha</TableCell>
                  <TableCell width={isMobile ? "30%" : "35%"}>Actividades</TableCell>
                  <TableCell width={isMobile ? "15%" : "13%"}>Estado</TableCell>
                  <TableCell width={isMobile ? "15%" : "15%"}>Observaciones</TableCell>
                  <TableCell width="5%">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(seguimientos) ? seguimientos : []).map((sesion) => {
                  const objetivo = objetivosDisponibles.find(o => o.id === sesion.objetivo_id);
                  
                  return (
                    <TableRow key={sesion.id}>
                      <TableCell>
                        <Box>
                          {objetivo ? (
                            <>
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                📋 {objetivo.area_nombre}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Obj. {objetivo.objetivo_numero}:</strong> {
                                  isMobile && objetivo.objetivo_texto.length > 50 ? 
                                    objetivo.objetivo_texto.substring(0, 50) + '...' :
                                    objetivo.objetivo_texto
                                }
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="error">
                              ⚠️ Objetivo no encontrado
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {sesion.editando ? (
                          <DatePicker
                            value={sesion.fecha ? new Date(sesion.fecha) : null}
                            onChange={(newValue) => 
                              actualizarSeguimiento(sesion.id, 'fecha', 
                                newValue ? format(newValue, 'yyyy-MM-dd') : '')
                            }
                            renderInput={(params) => 
                              <TextField {...params} size="small" fullWidth />
                            }
                            disabled={disabled}
                          />
                        ) : (
                          <Typography variant="body2">
                            {sesion.fecha ? format(new Date(sesion.fecha), 'dd/MM/yy') : '-'}
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {sesion.editando ? (
                          <CampoModal
                            value={sesion.actividades || ''}
                            onChange={(newValue) => 
                              actualizarSeguimiento(sesion.id, 'actividades', newValue)
                            }
                            label={`Actividades - Sesión ${objetivo?.objetivo_numero || 'N/A'}`}
                            placeholder="Describa las actividades realizadas en esta sesión..."
                            disabled={disabled}
                            minHeight={200}
                            helperText="Registre todas las actividades, ejercicios y técnicas utilizadas"
                            sx={{ minHeight: '40px' }}
                          />
                        ) : (
                          <Typography variant="body2">
                            {sesion.actividades ? (
                              sesion.actividades.length > 50 ? 
                                sesion.actividades.substring(0, 50) + '...' :
                                sesion.actividades
                            ) : '-'}
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {sesion.editando ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={sesion.estado || ''}
                              onChange={(e) => 
                                actualizarSeguimiento(sesion.id, 'estado', e.target.value)
                              }
                              disabled={disabled}
                            >
                              <MenuItem value="">-</MenuItem>
                              <MenuItem value="logrado">Logrado</MenuItem>
                              <MenuItem value="en_proceso">En Proceso</MenuItem>
                              <MenuItem value="no_logrado">No Logrado</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            icon={getIconEstado(sesion.estado)}
                            label={
                              sesion.estado === 'logrado' ? 'Logrado' : 
                              sesion.estado === 'en_proceso' ? 'En Proceso' :
                              sesion.estado === 'no_logrado' ? 'No Logrado' : '-'
                            }
                            color={getColorEstado(sesion.estado)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      
                      <TableCell>
                        {sesion.editando ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CampoModal
                              value={sesion.observaciones || ''}
                              onChange={(newValue) => 
                                actualizarSeguimiento(sesion.id, 'observaciones', newValue)
                              }
                              label={`Observaciones - Sesión ${objetivo?.objetivo_numero || 'N/A'}`}
                              placeholder="Observaciones sobre progreso, dificultades, etc..."
                              disabled={disabled}
                              minHeight={180}
                              helperText="Registre observaciones cualitativas sobre el progreso y comportamiento"
                              sx={{ minHeight: '40px', flex: 1 }}
                            />
                          </Stack>
                        ) : (
                          <Typography variant="caption">
                            {sesion.observaciones ? (
                              sesion.observaciones.length > 30 ? 
                                sesion.observaciones.substring(0, 30) + '...' :
                                sesion.observaciones
                            ) : '-'}
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          {sesion.editando ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => guardarSeguimiento(sesion.id)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => 
                                actualizarSeguimiento(sesion.id, 'editando', true)
                              }
                              disabled={disabled}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => eliminarSeguimiento(sesion.id)}
                            disabled={disabled}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>No hay sesiones registradas.</strong><br/>
              Seleccione un objetivo arriba y haga clic en "Agregar Sesión" para comenzar.
            </Typography>
          </Alert>
        )}
        
        {/* Diálogo de selección múltiple */}
        <DialogSeleccionMultiple 
          open={dialogSeleccionOpen}
          onClose={() => setDialogSeleccionOpen(false)}
          objetivos={objetivosDisponibles}
          onConfirmar={agregarSesionesSeleccionadas}
          loading={loading}
        />
      </Box>
    </LocalizationProvider>
  );
};

// Componente para diálogo de selección múltiple
const DialogSeleccionMultiple = ({ open, onClose, objetivos, onConfirmar, loading }) => {
  const [seleccionados, setSeleccionados] = useState([]);
  
  const handleToggle = (objetivoId) => {
    setSeleccionados(prev => 
      prev.includes(objetivoId) 
        ? prev.filter(id => id !== objetivoId)
        : [...prev, objetivoId]
    );
  };
  
  const handleConfirmar = () => {
    onConfirmar(seleccionados);
    setSeleccionados([]);
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Agregar Múltiples Sesiones
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Seleccione uno o varios objetivos para crear sesiones simultáneamente.
          Puede seleccionar el mismo objetivo múltiples veces si necesita varias sesiones.
        </Alert>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          Object.entries(
            objetivos.reduce((acc, obj) => {
              if (!acc[obj.area_nombre]) acc[obj.area_nombre] = [];
              acc[obj.area_nombre].push(obj);
              return acc;
            }, {})
          ).map(([area, objetivosArea]) => (
            <Box key={area} mb={2}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                📋 {area}
              </Typography>
              {objetivosArea.map((objetivo) => (
                <FormControlLabel
                  key={`multiple_${objetivo.id}`}
                  control={
                    <Checkbox
                      checked={seleccionados.includes(objetivo.id)}
                      onChange={() => handleToggle(objetivo.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        <strong>Objetivo {objetivo.objetivo_numero}:</strong> {objetivo.objetivo_texto}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button 
          variant="contained" 
          onClick={handleConfirmar}
          disabled={seleccionados.length === 0}
        >
          Crear {seleccionados.length} Sesión{seleccionados.length !== 1 ? 'es' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TablaSeguimientoMensual;